import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SEOData from '@/components/feature/SEOData';
import { useScrollToTop } from '@/hooks/useScrollToTop';

export default function About() {
  useScrollToTop();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9rhahvnahb7382ovmj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed && (parsed as Record<string, unknown>).meta && ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message as string) ||
          (parsed && (parsed as Record<string, unknown>).message as string) ||
          responseText ||
          '提交失败，请稍后重试';
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOData
        pageType="about"
        title="关于我们 - 龙禁漫 | 品牌介绍与联系方式"
        description="了解龙禁漫品牌故事与发展历程。龙禁漫专注正版漫画内容，为读者提供优质的在线阅读体验。查看品牌口碑评价与联系方式，加入龙禁漫漫画社区。"
        canonical="https://longjinman.com/about"
      />
      <Navbar />
      <div className="pt-16 md:pt-18">
        <Breadcrumbs />
      </div>
      <main>
        <section className="py-14 md:py-20 bg-background-50" aria-labelledby="about-hero-title">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 id="about-hero-title" className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-5">
              关于龙禁漫
            </h1>
            <p className="text-foreground-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              龙禁漫成立于2020年，是一家专注于正版漫画内容的综合阅读平台。
              我们致力于为漫画爱好者提供丰富、优质、便捷的在线阅读体验，
              让每一位读者都能在这里找到属于自己的精彩世界。
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100" aria-labelledby="brand-title">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 id="brand-title" className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">
              品牌故事
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              <div className="bg-background-50 rounded-xl p-6 md:p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-5">
                  <i className="ri-lightbulb-line text-primary-600 text-xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">我们的初心</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  龙禁漫的创始团队由一群热爱漫画的年轻人组成。我们深知漫画爱好者在寻找高质量正版漫画时的困难——资源分散、画质参差、更新不及时。于是，我们决定打造一个汇聚正版漫画资源的综合平台，让每一位读者都能轻松找到并享受优质漫画内容。
                </p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 md:p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-5">
                  <i className="ri-trophy-line text-accent-600 text-xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">发展历程</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  自2020年上线以来，龙禁漫从最初的数百部漫画资源发展到如今超过10,000部正版漫画的规模。我们与国内外多家知名漫画出版社和独立作者建立了深度合作关系，平台累计注册用户已突破500万，日均活跃读者超过80万人。
                </p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 md:p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 mb-5">
                  <i className="ri-shield-check-line text-secondary-600 text-xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">正版承诺</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  我们深知正版内容对于漫画产业健康发展的重要性。龙禁漫始终坚持正版授权合作模式，所有平台上的漫画内容均获得了版权方的正式授权。我们不仅为读者提供高质量的阅读体验，也为创作者提供合理的收益回报，推动漫画产业的良性循环。
                </p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 md:p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-5">
                  <i className="ri-rocket-line text-primary-600 text-xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">未来展望</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  龙禁漫将继续扩展漫画资源库，引入更多国内外优质漫画作品。我们计划推出个性化推荐系统、社区互动功能以及创作者扶持计划，致力于成为华语世界最优质的漫画阅读平台，让每一位漫画爱好者都能在龙禁漫找到归属感。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50" aria-labelledby="reputation-title">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 id="reputation-title" className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              <div className="bg-background-100 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  "龙禁漫的画质真的太棒了！每一页都清晰到可以看到画师的笔触细节。而且更新速度很快，追更体验非常好。已经推荐给身边所有喜欢看漫画的朋友了。"
                </p>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center font-heading text-sm font-bold text-primary-600">张</span>
                  <div>
                    <p className="text-foreground-900 text-sm font-semibold">漫画达人小张</p>
                    <p className="text-foreground-500 text-xs">资深漫画读者 · 3年会员</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  "从大学开始用龙禁漫看漫画，现在已经工作了还在用。最喜欢的是它的分类功能，找漫画特别方便。而且离线下载功能在通勤路上太实用了！"
                </p>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center font-heading text-sm font-bold text-accent-600">李</span>
                  <div>
                    <p className="text-foreground-900 text-sm font-semibold">小李漫评</p>
                    <p className="text-foreground-500 text-xs">漫画博主 · 5年会员</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  "作为一个漫画家，很高兴看到龙禁漫这样认真做正版的平台。它让更多读者接触到优质漫画，也让我们创作者获得了应有的尊重和回报。"
                </p>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center font-heading text-sm font-bold text-secondary-600">王</span>
                  <div>
                    <p className="text-foreground-900 text-sm font-semibold">王老师</p>
                    <p className="text-foreground-500 text-xs">签约漫画家</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100" aria-labelledby="contact-title">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-12">
              <h2 id="contact-title" className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                有任何问题或建议？欢迎随时与我们取得联系
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-10">
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                  <i className="ri-mail-line text-primary-600 text-xl"></i>
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-foreground-500 text-xs">service@longjinman.com</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-4">
                  <i className="ri-phone-line text-accent-600 text-xl"></i>
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                <p className="text-foreground-500 text-xs">400-888-6666</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-4">
                  <i className="ri-time-line text-secondary-600 text-xl"></i>
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                <p className="text-foreground-500 text-xs">周一至周五 9:00-18:00</p>
              </div>
            </div>

            <div className="bg-background-50 rounded-xl p-6 md:p-8">
              {formStatus === 'success' ? (
                <div className="text-center py-10">
                  <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                    <i className="ri-check-line text-primary-600 text-2xl"></i>
                  </span>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">感谢您的留言！</h3>
                  <p className="text-foreground-500 text-sm">我们已收到您的信息，客服团队将在24小时内与您联系。</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-6 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form id="contact-form-about">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">发送留言</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请选择或输入留言主题"
                    />
                  </div>
                  <div className="mb-5">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                    <p className="text-foreground-400 text-xs mt-1">最多500个字符</p>
                  </div>
                  <div className="contact-alt-wrapper">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  {formStatus === 'error' && formError && (
                    <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                      <p className="text-primary-700 text-sm">{formError}</p>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full md:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap"
                  >
                    {submitting ? '提交中...' : '发送留言'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}